import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import useStableId from '../../../hooks/useStableId';
import { applyTypography, TYPOGRAPHY_VARIANTS } from '../../styled/theme/typography-utils';

const ToggleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
`;

const ToggleWrapper = styled.div`
    display: flex;
    width: 32px;
    min-width: 32px;
    max-width: 32px;
    min-height: 16px;
    padding: 2px 10px 2px 2px;
    align-items: center;
    gap: 10px;
    border-radius: 100px;
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;
    position: relative;
    
    /* Ensure minimum touch target size (44px) */
    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 44px;
        height: 44px;
        transform: translate(-50%, -50%);
        z-index: -1;
    }
    
    /* Respect reduced motion preferences */
    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
    
    /* Default state */
    background-color: ${({ theme }) => theme.components.lyt_toggle.background.default};
    
    /* Hover state */
    &:hover {
        background-color: ${({ theme, checked, disabled }) => {
            if (disabled) return theme.components.lyt_toggle.background.disabled;
            if (checked) return theme.components.lyt_toggle.background.toggledHover;
            return theme.components.lyt_toggle.background.hover;
        }};
    }
    
    /* Checked state */
    ${({ checked, theme }) => checked && `
        background-color: ${theme.components.lyt_toggle.background.toggled};
    `}
    
    /* Disabled state */
    ${({ disabled, theme }) => disabled && `
        background-color: ${theme.components.lyt_toggle.background.disabled};
        cursor: not-allowed;
        opacity: 0.6;
    `}
    
    /* Checked + Disabled state */
    ${({ checked, disabled, theme }) => checked && disabled && `
        background-color: ${theme.components.lyt_toggle.background.toggledDisabled};
    `}
    
    /* Enhanced focus indicators */
    &:focus-visible {
        outline: 1px solid ${({ theme }) => theme.components.lyt_toggle.border.focus};
        outline-offset: 1px;
    }
    
    /* High contrast mode support */
    @media (prefers-contrast: high) {
        border: 2px solid;
        &:focus-visible {
            outline: 3px solid;
            outline-offset: 2px;
        }
    }
    
    /* Windows High Contrast Mode */
    @media screen and (-ms-high-contrast: active) {
        border: 2px solid;
        &:focus {
            outline: 2px solid;
        }
    }
`;

const HiddenInput = styled.input`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;

const ToggleThumb = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.components.lyt_toggle.thumb.background};
    transition: transform 0.2s ease-in-out;
    transform: ${({ checked }) => checked ? 'translateX(16px)' : 'translateX(0px)'};
    position: absolute;
    left: 2px;
    top: 50%;
    margin-top: -6px;
    
    /* Respect reduced motion preferences */
    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
    
    /* High contrast mode support */
    @media (prefers-contrast: high) {
        border: 1px solid;
    }
    
    /* Windows High Contrast Mode */
    @media screen and (-ms-high-contrast: active) {
        border: 1px solid;
    }
`;

const Label = styled.span`
    font-size: 14px;
    color: ${({ theme }) => theme.components.lyt_toggle.label.color};
    user-select: none;
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
`;

const SubLabel = styled.span`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_NORMAL)}
    color: ${({ theme }) => theme.components?.lyt_toggle?.sublabel?.color || '#595A5D'};
    user-select: none;
    display: block;
    margin-top: 2px;
`;

// Screen reader only text for state announcements
const ScreenReaderText = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;

const LabelContainer = styled.div`
    flex: 1;
    cursor: pointer;
    ${({ disabled, theme }) => disabled && `
        color: ${theme.components?.lyt_toggle?.sublabel?.color || theme.components.lyt_toggle.label.disabled};
        opacity: 0.6;
        cursor: not-allowed;
    `}
`;

/**
 * Accessible toggle component that follows LYT design system patterns and WCAG 2.1 AA standards
 * 
 * @param {Object} props - Component props
 * @param {string} props.id - Unique identifier for the toggle
 * @param {string} props.name - Name attribute for the toggle input
 * @param {boolean} props.checked - Whether the toggle is checked/on
 * @param {function} props.onChange - Handler for toggle state changes
 * @param {boolean} props.disabled - Whether the toggle is disabled
 * @param {boolean} props.required - Whether the toggle is required
 * @param {string} props.label - Label text for the toggle
 * @param {string} props.sublabel - Sublabel text displayed under the main label
 * @param {string} props.labelPosition - Position of label relative to toggle ("left" | "right")
 * @param {string} props.ariaLabel - Accessible label (when visual label isn't sufficient)
 * @param {string} props.ariaLabelledBy - ID of an element that labels this toggle
 * @param {string} props.ariaDescribedBy - ID of an element that describes this toggle
 * @param {string} props.description - Additional description for screen readers
 * @param {string} props.className - Additional class name for styling
 * @param {string} props.testId - Test ID for testing
 * @param {function} props.onFocus - Handler for focus events
 * @param {function} props.onBlur - Handler for blur events
 */
const LytToggle = ({
    id,
    name,
    checked,
    onChange,
    disabled = false,
    required = false,
    label,
    sublabel,
    labelPosition = 'right',
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
    description,
    className,
    testId,
    onFocus,
    onBlur,
}) => {
    // Generate a unique ID if one isn't provided
    const uniqueId = useStableId();
    const toggleId = id || `toggle-${uniqueId}`;
    const descriptionId = description ? `${toggleId}-description` : undefined;
    
    // Combine description ID with ariaDescribedBy
    const combinedDescribedBy = [ariaDescribedBy, descriptionId]
        .filter(Boolean)
        .join(' ') || undefined;
    
    const handleChange = (event) => {
        if (!disabled) {
            onChange(event.target.checked);
        }
    };
    
    const handleKeyDown = (event) => {
        // Handle space and enter keys for accessibility
        if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            if (!disabled) {
                onChange(!checked);
            }
        }
    };
    
    const handleClick = () => {
        // Allow clicks on any part of the toggle (wrapper or thumb)
        if (!disabled) {
            onChange(!checked);
        }
    };
    
    const handleLabelClick = () => {
        if (!disabled) {
            onChange(!checked);
        }
    };
    
    // Helper function to render label content
    const renderLabelContent = () => {
        if (!label) return null;
        
        return (
            <LabelContainer 
                onClick={handleLabelClick}>
                <Label 
                    id={`${toggleId}-label`}
                    disabled={disabled}
                >
                    {label}
                    {required && <span aria-label="required"> *</span>}
                </Label>
                {sublabel && (
                    <SubLabel 
                        disabled={disabled}
                    >
                        {sublabel}
                    </SubLabel>
                )}
            </LabelContainer>
        );
    };

    return (
        <ToggleContainer className={className} data-testid={testId}>
            {labelPosition === 'left' && renderLabelContent()}
            <ToggleWrapper 
                role="switch"
                aria-checked={checked}
                aria-labelledby={label ? `${toggleId}-label` : ariaLabelledBy}
                aria-describedby={combinedDescribedBy}
                aria-label={ariaLabel}
                aria-disabled={disabled}
                aria-required={required}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={handleKeyDown}
                onClick={handleClick}
                onFocus={onFocus}
                onBlur={onBlur}
                checked={checked}
                disabled={disabled}
            >
                <HiddenInput
                    type="checkbox"
                    id={toggleId}
                    name={name}
                    checked={checked}
                    onChange={handleChange}
                    disabled={disabled}
                    required={required}
                    tabIndex={-1}
                    aria-hidden="true"
                />
                <ToggleThumb checked={checked} />
                <ScreenReaderText aria-live="polite">
                    {checked ? 'On' : 'Off'}
                </ScreenReaderText>
            </ToggleWrapper>
            {labelPosition === 'right' && renderLabelContent()}
            {description && (
                <ScreenReaderText id={descriptionId}>
                    {description}
                </ScreenReaderText>
            )}
        </ToggleContainer>
    );
};

LytToggle.propTypes = {
    /** Unique identifier for the toggle */
    id: PropTypes.string,
    /** Name attribute for the toggle input */
    name: PropTypes.string,
    /** Whether the toggle is checked/on */
    checked: PropTypes.bool.isRequired,
    /** Handler for toggle state changes */
    onChange: PropTypes.func.isRequired,
    /** Whether the toggle is disabled */
    disabled: PropTypes.bool,
    /** Whether the toggle is required */
    required: PropTypes.bool,
    /** Label text for the toggle */
    label: PropTypes.string,
    /** Sublabel text displayed under the main label */
    sublabel: PropTypes.string,
    /** Position of label relative to toggle */
    labelPosition: PropTypes.oneOf(['left', 'right']),
    /** Accessible label (when visual label isn't sufficient) */
    ariaLabel: PropTypes.string,
    /** ID of an element that labels this toggle */
    ariaLabelledBy: PropTypes.string,
    /** ID of an element that describes this toggle */
    ariaDescribedBy: PropTypes.string,
    /** Additional description for screen readers */
    description: PropTypes.string,
    /** Additional class name for styling */
    className: PropTypes.string,
    /** Test ID for testing */
    testId: PropTypes.string,
    /** Handler for focus events */
    onFocus: PropTypes.func,
    /** Handler for blur events */
    onBlur: PropTypes.func,
};

export default LytToggle; 